import { FetchError } from "@decibeltrade/sdk";

export function getIsFetchError(error: unknown) {
  return (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.includes("Failed to fetch")
  );
}

/**
 * Browser network fetch failure (no response received). Chromium emits
 * `"Failed to fetch"`; WebKit (Safari, iOS WKWebView, in-app browsers) emits
 * `"Load failed"`. Same class of error — user-side network noise (mobile
 * WebView focus loss, CORS, adblockers). Real backend outages surface in
 * Grafana HTTP error-rate dashboards, not Sentry frontend counts.
 */
export function isBrowserNetworkFetchError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message === "Failed to fetch" || error.message === "Load failed")
  );
}

export function isFetchErrorWithStatus(error: unknown, statuses: readonly number[]) {
  return error instanceof FetchError && statuses.includes(error.status);
}

/**
 * A response that failed Zod validation. Structural rather than `instanceof`:
 * the SDK bundles its own zod, and it rethrows the ZodError wrapped as `.cause`.
 */
export function isSchemaMismatchError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current != null && typeof current === "object" && !seen.has(current)) {
    seen.add(current);
    const obj = current as { name?: unknown; issues?: unknown; cause?: unknown };
    if (obj.name === "ZodError" && Array.isArray(obj.issues)) return true;
    current = obj.cause;
  }
  return false;
}

/**
 * Collect every message-like string reachable from an error: its own
 * `message`, any string `error` field (SDK write results carry the on-chain
 * abort text there, e.g. placeSpotOrder's `{ success: false, error }`), and
 * the same fields down the `cause` chain. Use when a needle may be buried in
 * a wrapped error — `getErrorMessage` reads only the top-level message.
 */
export function getErrorMessageChain(error: unknown): string {
  const parts: string[] = [];
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current != null && !seen.has(current)) {
    seen.add(current);
    if (typeof current === "string") {
      parts.push(current);
      break;
    }
    if (typeof current !== "object") break;
    const obj = current as Record<string, unknown>;
    if (typeof obj.message === "string") parts.push(obj.message);
    if (typeof obj.error === "string") parts.push(obj.error);
    current = obj.cause;
  }
  return parts.join("\n");
}

export function getErrorMessage(error: unknown) {
  return error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
    ? error.message
    : typeof error === "string"
      ? error
      : undefined;
}
